import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserState } from "./types";
import { fetchEmployees, fetchUsers } from "./users-thunk";
import { ErrorType } from "@/shared/types";
import { AttractedStatusFilter, BlockStatusFilter, DeletedStatusFilter, IUser } from "@/shared/api/users";

const initialState: UserState = {
    users: [],
    isUsersLoading: false,
    usersError: null,
    isUsersFirstLoad: true,

    employeesError: null,
    employees: [],
    isEmployeesLoading: false,
    isEmployeesFirstLoad: true,

    usersTotalPages: 0,
    usersTotalCount: 0,
    usersSearch: null,
    usersGroup: null,
    usersBlockStatus: 'all',
    usersDeletedStatus: 'active',
    usersAttractedStatus: 'all',
    usersPage: 1,
    usersLimit: 50,

    employeesTotalPages: 0,
    employeesTotalCount: 0,
    employeesSearch: null,
    employeesGroup: null,
    employeesBlockStatus: 'all',
    employeesDeletedStatus: 'active',
    employeesPage: 1,
    employeesLimit: 50,

    selectedUsersForReport: [],
    isReportGenerationMode: false
}

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setUsersPage: (state, action: PayloadAction<number>) => {
            state.usersPage = action.payload
        },
        setEmployeesPage: (state, action: PayloadAction<number>) => {
            state.employeesPage = action.payload
        },
        setUsersGroup: (state, action: PayloadAction<string | null>) => {
            state.usersGroup = action.payload
        },
        setEmployeesGroup: (state, action: PayloadAction<string | null>) => {
            state.employeesGroup = action.payload
        },
        setUsersSearch: (state, action: PayloadAction<string | null>) => {
            state.usersSearch = action.payload?.length ? action.payload : null
        },
        setEmployeesSearch: (state, action: PayloadAction<string | null>) => {
            state.employeesSearch = action.payload?.length ? action.payload : null
        },
        setUsersBlockStatus: (state, action: PayloadAction<BlockStatusFilter>) => {
            state.usersBlockStatus = action.payload
        },
        setUsersDeletedStatus: (state, action: PayloadAction<DeletedStatusFilter>) => {
            state.usersDeletedStatus = action.payload
        },
        setUsersAttractedStatus: (state, action: PayloadAction<AttractedStatusFilter>) => {
            state.usersAttractedStatus = action.payload
            state.usersPage = 1
        },
        setEmployeesBlockStatus: (state, action: PayloadAction<BlockStatusFilter>) => {
            state.employeesBlockStatus = action.payload
        },
        setEmployeesDeletedStatus: (state, action: PayloadAction<DeletedStatusFilter>) => {
            state.employeesDeletedStatus = action.payload
        },
        addUserToReport: (state, action: PayloadAction<IUser>) => {
            if (!state.selectedUsersForReport.find(user => user.id === action.payload.id)) {
                state.selectedUsersForReport.push(action.payload)
            }
        },
        removeUserFromReport: (state, action: PayloadAction<string>) => {
            state.selectedUsersForReport = state.selectedUsersForReport.filter(user => user.id !== action.payload)
        },
        clearSelectedUsersForReport: (state) => {
            state.selectedUsersForReport = []
            state.isReportGenerationMode = false
        },
        setReportGenerationMode: (state, action: PayloadAction<boolean>) => {
            state.isReportGenerationMode = action.payload
            if (!action.payload) {
                state.selectedUsersForReport = []
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.isUsersLoading = true
                state.usersError = null
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.usersError = null
                state.usersTotalPages = action.payload.totalPages,
                    state.users = action.payload.items
                state.usersTotalCount = action.payload.totalCount
                state.isUsersFirstLoad = false
                state.isUsersLoading = false
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isUsersLoading = false
                state.usersError = action.payload as ErrorType ?? null
            })
            .addCase(fetchEmployees.pending, (state) => {
                state.isEmployeesLoading = true
                state.employeesError = null
            })
            .addCase(fetchEmployees.fulfilled, (state, action) => {
                state.employeesError = null
                state.employees = action.payload.items
                state.employeesTotalPages = action.payload.totalPages
                state.employeesTotalCount = action.payload.totalCount,
                    state.isEmployeesFirstLoad = false
                state.isEmployeesLoading = false
            })
            .addCase(fetchEmployees.rejected, (state, action) => {
                state.isEmployeesLoading = false
                state.employeesError = action.payload as ErrorType ?? null
            })
    },
})

export default usersSlice.reducer
export const { setEmployeesGroup, setEmployeesPage, setEmployeesSearch, setEmployeesBlockStatus, setEmployeesDeletedStatus, setUsersGroup, setUsersPage, setUsersSearch, setUsersBlockStatus, setUsersDeletedStatus, setUsersAttractedStatus, addUserToReport, removeUserFromReport, clearSelectedUsersForReport, setReportGenerationMode } = usersSlice.actions
